import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import PropTypes from 'prop-types';
import CommonTable from 'components/commonTable/CommonTable';

const AssignResultModal = ({ isOpen, onClose, result }) => {
  const {
    successCount = 0,
    failedCount = 0,
    unboundCount = 0,
    failed = [],
  } = result || {};

  const columnHelper = createColumnHelper();
  const failedColumns = [
    columnHelper.accessor((row) => row?.merchantId, {
      id: 'merchantId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Merchant ID
        </Text>
      ),
      cell: (info) => (
        <Text fontSize="sm" fontWeight="600" color="secondaryGray.900">
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.name, {
      id: 'name',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Name
        </Text>
      ),
      cell: (info) => (
        <Text fontSize="sm" fontWeight="600" color="secondaryGray.900">
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.reason, {
      id: 'reason',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Reason
        </Text>
      ),
      cell: (info) => (
        <Text fontSize="sm" fontWeight="600" color="red.500">
          {info.getValue() ?? 'Unknown error'}
        </Text>
      ),
    }),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Assignment Result</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {/* Summary badges */}
          <Flex gap={4} mb={5}>
            <Flex
              align="center"
              gap={2}
              bg="green.50"
              px={4}
              py={3}
              borderRadius="md"
              flex={1}
            >
              <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                {successCount}
              </Badge>
              <Text fontSize="sm" fontWeight="medium" color="green.700">
                Assigned successfully
              </Text>
            </Flex>

            {unboundCount > 0 && (
              <Flex
                align="center"
                gap={2}
                bg="orange.50"
                px={4}
                py={3}
                borderRadius="md"
                flex={1}
              >
                <Badge colorScheme="orange" fontSize="md" px={2} py={1}>
                  {unboundCount}
                </Badge>
                <Text fontSize="sm" fontWeight="medium" color="orange.700">
                  Unbound successfully
                </Text>
              </Flex>
            )}

            {failedCount > 0 && (
              <Flex
                align="center"
                gap={2}
                bg="red.50"
                px={4}
                py={3}
                borderRadius="md"
                flex={1}
              >
                <Badge colorScheme="red" fontSize="md" px={2} py={1}>
                  {failedCount}
                </Badge>
                <Text fontSize="sm" fontWeight="medium" color="red.700">
                  Failed to assign
                </Text>
              </Flex>
            )}
          </Flex>

          {/* Failed merchants table */}
          {failed.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={3}>
                Failed Merchants
              </Text>
              <CommonTable
                columns={failedColumns}
                data={failed}
                isLoading={false}
                keyValue="merchantId"
                dynamicWidth={{
                  merchantId: '120px',
                  name: '180px',
                  reason: '250px',
                }}
              />
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            onClick={onClose}
          >
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

AssignResultModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  result: PropTypes.shape({
    successCount: PropTypes.number,
    failedCount: PropTypes.number,
    unboundCount: PropTypes.number,
    failed: PropTypes.arrayOf(
      PropTypes.shape({
        merchantId: PropTypes.number,
        name: PropTypes.string,
        reason: PropTypes.string,
      }),
    ),
  }),
};

AssignResultModal.defaultProps = {
  result: null,
};

export default AssignResultModal;
